import type { QueryFunction, QueryKey } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo } from 'react'

import { statusTelemetryTrpcClient } from '@/lib/trpc'

import type {
  StatusRequestKind,
  StatusTelemetrySample,
} from '@/lib/status-telemetry'
import {
  attachStatusTelemetryLifecycle,
  createStatusTelemetrySessionId,
  resolveStatusTelemetrySampleRate,
  StatusTelemetryCollector,
} from '@/lib/status-telemetry'

interface StatusLikeResult {
  game?: { status?: string }
  statusVersion?: string
}

interface StatusTelemetryGameScopeOptions {
  createSessionId?: () => string
  report?: (input: {
    clientSampleRate: number
    clientSessionId: string
    gameId: string
    samples: StatusTelemetrySample[]
  }) => Promise<unknown>
  sampleRate?: number
}

export function createStatusTelemetryGameScope(
  gameId: string | undefined,
  options: StatusTelemetryGameScopeOptions = {},
) {
  const sampleRate
    = options.sampleRate ?? resolveStatusTelemetrySampleRate()
  const createSessionId
    = options.createSessionId ?? createStatusTelemetrySessionId
  const report
    = options.report
      ?? (input =>
        statusTelemetryTrpcClient.observabilityRouter.reportStatusDelivery.mutate(
          input,
        ))
  let clientSessionId: string | null = null
  let appliedVersion: string | null = null
  let lastStatus: string | undefined
  let pendingApply: (() => void) | null = null

  return {
    collector: new StatusTelemetryCollector({
      flush: async (samples) => {
        if (!gameId)
          return
        clientSessionId ??= createSessionId()
        await report({
          clientSampleRate: sampleRate,
          clientSessionId,
          gameId,
          samples,
        })
      },
      sampleRate,
    }),
    lastStatus: () => lastStatus,
    markApplied: (statusVersion: string | null) => {
      if (statusVersion !== null && appliedVersion === statusVersion)
        return
      appliedVersion = statusVersion
      pendingApply?.()
      pendingApply = null
    },
    rememberStatus: (status: string | undefined) => {
      lastStatus = status
    },
    setPendingApply: (apply: () => void) => {
      pendingApply = apply
    },
  }
}

export function statusRequestKind(
  previousStatus: string | undefined,
): StatusRequestKind {
  return previousStatus === 'completed' || previousStatus === 'error'
    ? 'terminal'
    : 'active'
}

export function useStatusTelemetry(gameId: string | undefined) {
  const scope = useMemo(
    () => createStatusTelemetryGameScope(gameId),
    [gameId],
  )
  const { collector } = scope

  useEffect(() => {
    const detachLifecycle = attachStatusTelemetryLifecycle(collector)
    return () => {
      detachLifecycle()
      void collector.flushNow()
    }
  }, [collector])

  const instrument = useCallback(
    <TResult extends StatusLikeResult, TKey extends QueryKey>(
      queryFn: QueryFunction<TResult, TKey>,
    ): QueryFunction<TResult, TKey> => {
      return async (context) => {
        const request = collector.beginRequest(
          statusRequestKind(scope.lastStatus()),
        )
        try {
          const result = await queryFn(context)
          request.firstByte()
          scope.rememberStatus(result?.game?.status)
          request.complete({
            outcome: 'ok',
            statusVersion: result?.statusVersion ?? null,
            transport: 'legacy',
          })
          scope.setPendingApply(() => request.applied())
          return result
        }
        catch (error) {
          request.complete({ outcome: 'error' })
          throw error
        }
      }
    },
    [collector, scope],
  )

  const markApplied = useCallback(
    (statusVersion: string | null) => {
      scope.markApplied(statusVersion)
    },
    [scope],
  )

  const markInvalidated = useCallback(() => {
    collector.markInvalidated()
  }, [collector])

  return { instrument, markApplied, markInvalidated }
}
